import { ControllerFlowAPI } from 'yoshi-flow-editor-runtime/build/cjs/flow-api/ViewerScript';
import { SelectedPaymentOption } from '../../../components/ChallengesPage/Widget/components/Pricing/interfaces';
import { isNeedToAbortJoinFlowAfterLogin } from './isNeedToAbortJoinFlowAfterLogin';
import { createParticipant } from './createParticipant';
import { ParticipantState } from '@wix/ambassador-challenge-service-web/types';
import { payForChallenge } from './payForChallenge';
import {
  getTimeZone,
  getUserType,
  navigateToThankYouPage,
  promptLogin,
  toServerStartDate,
} from './userContextHelpers';
import userTypeHandlers from './userTypeHandlers';
import { IUserProviderProps } from '../UserProvider';
import { loadingPropsMap } from '../../Loading/loadingPropsMap';
import { FedopsInteraction } from '../../../components/ChallengesPage/Widget/constants';

export async function joinToChallenge(
  flowAPI: ControllerFlowAPI,
  userProvider: IUserProviderProps,
  selectedPaymentOption: SelectedPaymentOption,
  startDate?: string,
) {
  const startDateFormatted = toServerStartDate(startDate);
  const timeZone = getTimeZone(flowAPI);
  const loadingProviderProps = loadingPropsMap(flowAPI);

  loadingProviderProps.showLoader();

  if (!flowAPI.controllerConfig.wixCodeApi.user.currentUser.loggedIn) {
    try {
      await promptLogin(flowAPI);
      await userProvider.updateParticipant();
    } catch (e) {
      loadingProviderProps.hideLoader();
      return;
    }

    if (await isNeedToAbortJoinFlowAfterLogin(flowAPI)) {
      flowAPI.fedopsLogger.interactionEnded(
        FedopsInteraction.JoinUserToChallenge,
      );
      loadingProviderProps.hideLoader();
      return;
    }
  }

  flowAPI.fedopsLogger.interactionStarted(
    FedopsInteraction.JoinUserToChallenge,
  );

  const currentParticipantUserType = getUserType(
    { loggedIn: true },
    userProvider.participant,
  );

  if (userTypeHandlers.isJoinedAlready(currentParticipantUserType)) {
    flowAPI.fedopsLogger.interactionEnded(
      FedopsInteraction.JoinUserToChallenge,
    );
    loadingProviderProps.hideLoader();
    return;
  }

  const participant = await createParticipant({
    timeZone,
    userProvider,
    startDateFormatted,
    flowAPI,
    selectedPaymentOption,
  });

  if (participant) {
    switch (participant.transitions[0].state) {
      case ParticipantState.PAYMENT_REQUESTED:
      case ParticipantState.PAYMENT_STARTED:
        const { userJoined } = await payForChallenge(
          flowAPI,
          userProvider,
          selectedPaymentOption,
          participant.id,
          startDateFormatted,
          timeZone,
        );
        if (userJoined) {
          return navigateToThankYouPage(flowAPI);
        }
        return;
      case ParticipantState.JOIN_REQUESTED:
      case ParticipantState.JOINED:
      case ParticipantState.RUNNING:
        return navigateToThankYouPage(flowAPI);
      default:
        flowAPI?.sentryMonitor?.captureMessage(
          `[challenge] Unexpected participant state "${participant?.transitions[0].state}" during join.`,
        );
        loadingProviderProps.hideLoader();
    }
  }
  loadingProviderProps.hideLoader();
  console.error('[CHALLENGE] Unable to join user');
}
